import fuzzysort from 'fuzzysort';
import React, { useEffect, useRef, useState } from 'react';
import { ApidaeGridItem, fetchApidaeSelectionObjects } from '../../tools/apidaeListAPI';
import { ApidaeGridSortMode, sortApidaeGridItems } from '../../tools/apidaeGridSort';
import { isApidaeEventType } from '../../tools/apidaeEventDates';
import { RequirementsProps } from '../../tools/context';
import { replaceUrlParam } from '../../tools/tools';
import OfferSlide, { getDiscountTags } from '../Carousel/OfferSlide';
import Map from '../GridComponents/Map';
import Pagination from '../GridComponents/Pagination';
import { SearchBar } from '../LightSearchGrid/SearchBar';
import { CategoryPopupData, FiltersConfigurationType } from '../LightSearchGrid/SearchBar/categoryPopup';

export interface ApidaeSelectionGridProps {
  componentStates: ApidaeGridItem[] | null;
  requirements: RequirementsProps;
  filtersConfiguration: FiltersConfigurationType;
  mapOpenedByDefault?: boolean;
  resultsPerPage?: number;
  defaultSort?: ApidaeGridSortMode | null;
  showDateFilter?: boolean;
}

const roundGrade = (input: number) => (isNaN(input) ? 0 : Math.round(input * 10) / 10);

function appendCoupon(baseUrl: string, coupon: string | undefined) {
  if (!coupon) return baseUrl;
  const sep = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${sep}coupon=${encodeURIComponent(coupon)}`;
}

const Renderer = (props: ApidaeSelectionGridProps) => {
  const norm = (it: string) => it.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const hasEventItems = (props.componentStates || []).some((it) => isApidaeEventType(it?.apidaeType));
  const getUrlMapOpened = (): boolean | undefined => {
    if (typeof window === 'undefined') return undefined;
    const rawMapOpened = new URLSearchParams(window.location.search).get('mapOpened');
    if (rawMapOpened === 'true') return true;
    if (rawMapOpened === 'false') return false;
    return undefined;
  };

  const rawList = props.componentStates || [];

  const componentStates = useRef<ApidaeGridItem[]>(
    sortApidaeGridItems(
      rawList.filter((c) => !!c?.categslug),
      props.defaultSort
    ).map((d) => ({
        ...d,
        norm: norm(`${d.storeName || ''}_${d.city}_${d.categslug}`),
        note: roundGrade(d.note),
      }))
  );

  const filters: CategoryPopupData = { categ: [], labels: [], filters: [] };

  const urlMapOpened = getUrlMapOpened();
  const initialMapOpened = urlMapOpened ?? (props.mapOpenedByDefault === true);

  const [showMap, setShowMap] = useState<boolean>(initialMapOpened);
  const [isClient, setIsClient] = useState(false);
  const [state, setState] = useState<ApidaeGridItem[]>(componentStates.current);
  const [page, setPage] = useState<number>(props.requirements.page || 0);
  const [input, setInput] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState<{ from?: string | null; to?: string | null }>({
    from: null,
    to: null,
  });
  const pageSize = Math.min(Math.max(props.resultsPerPage ?? 15, 1), 50);
  const nbPages = Math.max(1, Math.ceil(state.length / pageSize));
  const safePage = Math.min(Math.max(page, 0), nbPages - 1);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    window.history.replaceState(
      {},
      '',
      replaceUrlParam('mapOpened', showMap, window.location.href)
    );
  }, [showMap]);

  const handleChangePage = async (p: number) => {
    setPage(p);
  };

  useEffect(() => {
    setPage(0);
    const newUrl = replaceUrlParam('page', null, window.location.href);
    window.history.pushState({}, '', newUrl);

    let targets = componentStates.current;

    if (input) {
      const fuz = fuzzysort.go(norm(input), targets, {
        key: ['norm'],
        limit: 300,
        threshold: -100,
      });
      targets = fuz.map((f) => f.obj);
    }

    const from = dateFilter?.from ?? null;
    const to = dateFilter?.to ?? null;
    const hasDate = !!(from || to);
    if (hasDate) {
      targets = targets.filter((t) => {
        const p = t?.eventPeriod;
        if (!p?.dateDebut || !p?.dateFin) return false;
        // Overlap test (inclusive) in ISO yyyy-mm-dd
        const startOk = !to || p.dateDebut <= to;
        const endOk = !from || p.dateFin >= from;
        return startOk && endOk;
      });
    }

    setState(targets);
  }, [input, dateFilter?.from, dateFilter?.to]);

  const coupon = props.requirements?.coupon;

  return (
    <div className="lightSearchGrid apidaeSelectionGrid">
      <SearchBar
        requirements={props.requirements}
        mapGridCallback={(e) => setShowMap(e)}
        mapOpened={showMap}
        handleInput={(i) => setInput(i)}
        categData={filters}
        filtersConfiguration={props.filtersConfiguration}
        categoryCallback={() => {}}
        hideCategoryFilter
        goAsCaret
        showDateFilter={props.showDateFilter && hasEventItems}
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
      />
      <div className="noResult">
        {state?.length
          ? `${state.length} résultat${state.length > 1 ? 's' : ''}`
          : 'Pas de résultat'}
      </div>
      <div className="filters" />
      {isClient && showMap && <Map results={state} requirements={props.requirements} />}
      {!!state.length && !showMap && (
        <>
          <div className="grid">
            <div className="results">
              {state.slice(pageSize * safePage, pageSize * (safePage + 1)).map((r) => (
                <OfferSlide
                  key={r.id}
                  image={r.urlimg}
                  fallbackImage={`https://tourismebyca.twic.pics/static/${props.requirements?.partner}/v2/categSearch/${r.categslug}.jpg`}
                  fallbackImage2={`https://tourismebyca.twic.pics/static/tca/v2/categSearch/${r.categslug}.png`}
                  category={r.categslug}
                  title={r.storeName}
                  note={r.note}
                  place={r.city}
                  discount={r.gifts ? getDiscountTags(r.gifts) : []}
                  url={appendCoupon(r.detailUrl, coupon)}
                  eventPeriod={r.eventPeriod}
                  requirements={props.requirements}
                />
              ))}
            </div>
          </div>
          <Pagination
            nbPages={nbPages}
            currentPage={safePage}
            requirements={props.requirements}
            handleChangePage={handleChangePage}
          />
        </>
      )}
    </div>
  );
};

const preloader = async (
  data: { selectionId?: number; maxResults?: number; defaultSort?: ApidaeGridSortMode | null },
  requirements: RequirementsProps
) => {
  const selectionId = Number(data?.selectionId);
  if (!selectionId || Number.isNaN(selectionId)) {
    return [];
  }
  const items = await fetchApidaeSelectionObjects(selectionId, requirements.config?.apidae, {
    count: data.maxResults ?? 200,
  });
  return sortApidaeGridItems(items, data.defaultSort);
};

const ApidaeSelectionGrid = { Renderer, preloader };

export default ApidaeSelectionGrid;
export { Renderer, preloader };
